import { Component, inject, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogActions, MatDialogClose, MatDialogContent, MatDialogRef, MatDialogTitle } from '@angular/material/dialog';
import { Project } from 'src/app/entities/project/project';
import { Client } from 'src/app/entities/client/client';
import { User } from 'src/app/entities/user/user';
import { catchError, Observable } from 'rxjs';
import { ProjectRepository } from 'src/app/services/remote-data-service/repositories/project-repository/project.repository';
import { StoreService } from 'src/app/services/store-service/store.service';
import { Uuid } from 'src/app/entities/value-objects/uuid/uuid';
import { OperationType } from 'src/app/models/operation-types';
import { SnackbarService } from 'src/app/services/snackbar-service/snackbar.service';
import log from 'loglevel';
import { SelectListComponent } from '../../select-list/select-list.component';
import { BaseComponent } from '../../base-component/base.component';


/**
 * This component is used to create and update projects.
 *
 * It uses the MatDialog service to open a dialog for creating or updating a project.
 * It uses the ProjectRepository to create and update projects in the database.
 * It uses the StoreService to manage the state of the projects.
 */
@Component({
  selector: 'app-project-mutations',
  imports: [
    MatFormFieldModule,
    MatInputModule,
    MatCheckboxModule,
    MatSelectModule,
    MatIconModule,
    MatButtonModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatDialogActions,
    MatDialogClose,
    MatDialogTitle,
    MatDialogContent,
    SelectListComponent
  ],
  templateUrl: './project-mutations.component.html',
  styleUrl: './project-mutations.component.scss'
})
export class ProjectMutationsComponent extends BaseComponent implements OnInit {
  readonly dialogRef = inject(MatDialogRef<ProjectMutationsComponent>);

  protected override logger = log.getLogger('ProjectMutations');

  //operation = input.required<OperationType>()
  data = inject(MAT_DIALOG_DATA).operation as OperationType;
  projectId = inject(MAT_DIALOG_DATA).id as string;

  projects: Project[] = [];
  clients: Client[] = [];
  users: User[] = []

  projects$: Observable<Project[]>;
  clients$: Observable<Client[]>;
  users$: Observable<User[]>;

  title?: string;
  submitButton?: string;

  protected form = new FormGroup({
    name: new FormControl<string>('', Validators.required),
    color: new FormControl<string>('', Validators.required),
    archived: new FormControl<boolean>(false, Validators.required),
    active: new FormControl<boolean>(true, Validators.required),
    client: new FormControl<string>('', Validators.required),
    members: new FormControl<string[]>([], Validators.required),
    budget: new FormControl<number>(0, Validators.required),
  })

  constructor(
    protected projectRep: ProjectRepository,
    protected store: StoreService,
    private snackbarService: SnackbarService,
  ) {
    super();

    this.projects$ = this.store.projects;
    this.clients$ = this.store.clients;
    this.users$ = this.store.users;

    this.projects$.subscribe((projects) => {
      this.projects = projects;
    });

    this.clients$.subscribe((clients) => {
      this.clients = clients;

      this.setOptionsFromClients(clients);
    });

    this.users$.subscribe((users) => {
      this.users = users;

      this.setOptionsFromMembers(users);
    });
  }

  ngOnInit(): void {
    switch (this.data) {
      case OperationType.CREATE:
        this.title = 'Create Project';
        this.submitButton = 'Create';
        break;
      case OperationType.UPDATE: {
        this.title = 'Update Project';
        this.submitButton = 'Update';
        const currentProject = this.projects.find(p => p.id === this.projectId);

        if (currentProject) {
          this.form.patchValue({
            name: currentProject.name,
            color: currentProject.color,
            archived: currentProject.archived,
            active: currentProject.active,
            client: currentProject.client.id,
            members: currentProject.members?.map(member => member.id) || [],
            budget: currentProject.budget
          })

          this.setSelectedOption(this.selectListClients(), this.form.value.client);
          this.setSelectedOption(this.selectListMembers(), null, this.form.value.members);
        };
        break;
      }
    }
  }

  projectSubmit(): void {
    const form = this.form.value;

    const client = this.clients.find(client => client.id === form.client);

    const members: User[] = [];

    form.members?.forEach(memberId => {
      const user = this.users.find(member => member.id === memberId);
      if (user) {
        members.push(user)
      }
    })

    if (!client) return;

    const newProject = new Project(
      Uuid.generateId(),
      form.name || '',
      form.color || '#000',
      {
        delete: false,
        update: false,
        track: false
      },
      0,
      new Date(),
      form.active === undefined || form.active === null ? true : form.active,
      form.archived === undefined || form.archived === null ? true : form.archived,
      client,
      members,
      0
    );

    const updatedProject = new Project(
      this.projectId,
      form.name || '',
      form.color || '#000',
      {
        delete: false,
        update: false,
        track: false
      },
      0,
      new Date(),
      form.active === undefined || form.active === null ? true : form.active,
      form.archived === undefined || form.archived === null ? true : form.archived,
      client,
      members,
      0
    );

    switch (this.data) {
      case OperationType.CREATE:
        this.addProject(newProject);
        break;
      case OperationType.UPDATE: {
        this.updateProject(updatedProject);
        break;
      }
    }
  }

  addProject(project: Project): void {
    this.projectRep.addNewProject(project).pipe(
      catchError(() => {
        return []
      })
    ).subscribe(() => {
      this.store.addEntity(project);
      this.snackbarService.showSuccess('Project created');
    })
  }


  updateProject(project: Project): void {
    this.projectRep.updateProject(project).pipe(
      catchError(() => {
        return []
      })
    ).subscribe(() => {
      this.store.updateEntity(project);
      this.snackbarService.showSuccess('Project updated');
    })
  }
}
